import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "./auth/AuthContext";
import { AppLayout } from "./components/AppLayout";
import { LandingPage } from "./pages/LandingPage";
import { LoginCallback } from "./pages/LoginCallback";
import { DashboardPage } from "./pages/DashboardPage";
import { GroupDetailPage } from "./pages/GroupDetailPage";
import { GroupSettingsPage } from "./pages/GroupSettingsPage";
import { CreateExpensePage } from "./pages/CreateExpensePage";
import { CreateGroupPage } from "./pages/CreateGroupPage";
import { CreateSettlementPage } from "./pages/CreateSettlementPage";
import { CreateRecurringExpensePage } from "./pages/CreateRecurringExpensePage";
import { EditRecurringExpensePage } from "./pages/EditRecurringExpensePage";
import { ExpenseDetailPage } from "./pages/ExpenseDetailPage";
import { EditExpensePage } from "./pages/EditExpensePage";
import { GroupJoinPage } from "./pages/GroupJoinPage";
import { PublicExpenseDetailPage } from "./pages/PublicExpenseDetailPage";
import { AboutPage } from "./pages/AboutPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { SupportPage } from "./pages/SupportPage";
import { BrandPage } from "./pages/BrandPage";
import { ProfilePage } from "./pages/ProfilePage";
import { UserProfilePage } from "./pages/UserProfilePage";
import { ParticipantDetailPage } from "./pages/ParticipantDetailPage";
import { SettleUpPage } from "./pages/SettleUpPage";
import { AdminRoute } from "./pages/admin/AdminRoute";
import { AdminUsersPage } from "./pages/admin/AdminUsersPage";
import { AdminUserDetailPage } from "./pages/admin/AdminUserDetailPage";
import { LoadingScreen } from "./components/ui/loading-screen";

/**
 * Wraps every route under the authenticated app shell. Anonymous visitors
 * are routed to the landing page rather than redirected straight to Cognito
 * — that gives them a chance to see the product pitch and click "Continue
 * with Google" themselves, and avoids the StrictMode double-redirect race
 * that fires when the effect re-runs during the loading → anonymous
 * transition. The landing page CTA is the one entry point that mints a PKCE
 * verifier.
 */
function ProtectedRoute({ children }: { children: ReactNode }): ReactNode {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return <LoadingScreen />;
  }
  if (status === "authenticated") return children;
  // Preserve the original target so the post-login redirect lands the user
  // back where they started (not blanket /).
  const returnTo = encodeURIComponent(location.pathname + location.search);
  return <Navigate to={`/welcome?returnTo=${returnTo}`} replace />;
}

export function App(): ReactNode {
  return (
    <>
      <Routes>
        <Route path="/welcome" element={<LandingPage />} />
        <Route path="/login" element={<LoginCallback />} />
        <Route path="/groups/:groupId/join" element={<GroupJoinPage />} />
        <Route
          path="/groups/:groupId/join/expenses/:expenseId"
          element={<PublicExpenseDetailPage />}
        />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/brand" element={<BrandPage />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Routes>
                  <Route index element={<DashboardPage />} />
                  {/* /scan is gone — scanning is now inline on the New expense
                    screen. Redirect any stale links there. */}
                  <Route
                    path="scan"
                    element={<Navigate to="/expenses/new" replace />}
                  />
                  <Route path="profile" element={<ProfilePage />} />
                  {/* Another member's profile. The id in the path is the only
                    place the UUID surfaces on web (issue #452). */}
                  <Route path="users/:userId" element={<UserProfilePage />} />
                  <Route
                    path="admin"
                    element={
                      <AdminRoute>
                        <AdminUsersPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/users/:userId"
                    element={
                      <AdminRoute>
                        <AdminUserDetailPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="settings"
                    element={<Navigate to="/profile" replace />}
                  />
                  <Route path="expenses/new" element={<CreateExpensePage />} />
                  <Route path="groups/new" element={<CreateGroupPage />} />
                  <Route path="groups/:groupId" element={<GroupDetailPage />} />
                  <Route
                    path="groups/:groupId/settings"
                    element={<GroupSettingsPage />}
                  />
                  {/* One participant's transactions inside this group, and the
                    OWNER's "move them onto someone else" action (issue #448). */}
                  <Route
                    path="groups/:groupId/participants/:participantId"
                    element={<ParticipantDetailPage />}
                  />
                  {/* What's owed, as its own screen — the group page carries the
                    history instead (issue #453). */}
                  <Route
                    path="groups/:groupId/settle"
                    element={<SettleUpPage />}
                  />
                  {/* Nested "new expense" — declared before the
                    `:expenseId` route so `new` isn't captured as an id. */}
                  <Route
                    path="groups/:groupId/expenses/new"
                    element={<CreateExpensePage />}
                  />
                  <Route
                    path="groups/:groupId/settlements/new"
                    element={<CreateSettlementPage />}
                  />
                  <Route
                    path="groups/:groupId/expenses/:expenseId"
                    element={<ExpenseDetailPage />}
                  />
                  <Route
                    path="groups/:groupId/expenses/:expenseId/edit"
                    element={<EditExpensePage />}
                  />
                  <Route
                    path="groups/:groupId/recurring-expenses/new"
                    element={<CreateRecurringExpensePage />}
                  />
                  <Route
                    path="groups/:groupId/recurring-expenses/:recurringExpenseId"
                    element={<EditRecurringExpensePage />}
                  />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </AppLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
}
